import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRightLeft,
  Ban,
  CircleCheck,
  CircleSlash,
  Info,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import {
  ORG_ARCHIVE_GRACE_DAYS,
  ORG_MIGRATION_KIND_LABELS,
  listCounted,
  orgMigrationConfirmation,
  type OrgMigrationCandidates,
  type OrgMigrationCarry,
  type OrgMigrationItemRef,
  type OrgMigrationPlan,
  type OrgMigrationResult,
  type OrgSourceDisposition,
  type Organization,
  type Page,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { formatTimestamp } from '../lib/format';
import { useSession } from '../lib/session';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  Pill,
  SearchSelect,
  Spinner,
} from '../components/ui';

/**
 * Moving one tenant's equipment to another, and deciding what is left of the
 * one it came from.
 *
 * Four steps, in the order the decisions actually get made: who receives it,
 * what goes, what that costs — and only then the confirmations. The review step
 * is the one that matters. It lists every refusal beside every move, because an
 * operator who ticked eleven things and gets nine needs to know which two and
 * why BEFORE they type a company's name into a box, not afterwards from a
 * result screen.
 *
 * Superadmin only, and the page says so rather than half-working for anyone
 * else.
 */

type Step = 'choose' | 'review' | 'done';

function carryLine(items: OrgMigrationCarry[]): string {
  return items.length === 0 ? '' : listCounted(items);
}

export function OrgMigrationPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useSession();
  const isSuperadmin = session?.user.role === 'superadmin';

  const [targetOrgId, setTargetOrgId] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [step, setStep] = useState<Step>('choose');
  const [plan, setPlan] = useState<OrgMigrationPlan | null>(null);
  const [result, setResult] = useState<OrgMigrationResult | null>(null);

  const [disposition, setDisposition] = useState<OrgSourceDisposition>('keep');
  const [typedCount, setTypedCount] = useState('');
  const [typedTarget, setTypedTarget] = useState('');
  const [typedSource, setTypedSource] = useState('');
  const [typedPhrase, setTypedPhrase] = useState('');
  const [note, setNote] = useState('');

  const itemsQuery = useQuery({
    queryKey: ['org-migration-items', id],
    queryFn: () => api.get<OrgMigrationCandidates>(`/orgs/${encodeURIComponent(id)}/migration/items`),
    enabled: Boolean(id) && isSuperadmin,
  });

  const orgsQuery = useQuery({
    queryKey: ['orgs'],
    queryFn: () => api.get<Page<Organization>>('/orgs'),
    enabled: isSuperadmin,
  });

  const refs = useMemo<OrgMigrationItemRef[]>(
    () =>
      [...picked].map((key) => {
        const [kind, ...rest] = key.split(' ');
        return { kind: kind as OrgMigrationItemRef['kind'], id: rest.join(' ') };
      }),
    [picked],
  );

  const buildPlan = useMutation({
    mutationFn: () =>
      api.post<OrgMigrationPlan>(`/orgs/${encodeURIComponent(id)}/migration/plan`, {
        targetOrgId,
        items: refs,
      }),
    onSuccess: (built) => {
      setPlan(built);
      setTypedCount('');
      setTypedTarget('');
      setTypedSource('');
      setTypedPhrase('');
      setStep('review');
    },
  });

  const apply = useMutation({
    mutationFn: () =>
      api.post<OrgMigrationResult>(`/orgs/${encodeURIComponent(id)}/migration/apply`, {
        planId: plan?.id,
        confirmCount: Number(typedCount),
        confirmTargetName: typedTarget,
        sourceDisposition: disposition,
        confirmSourceName: typedSource || null,
        confirmPhrase: typedPhrase || null,
        note: note.trim() || null,
      }),
    onSuccess: async (applied) => {
      setResult(applied);
      setStep('done');
      await queryClient.invalidateQueries({ queryKey: ['orgs'] });
      await queryClient.invalidateQueries({ queryKey: ['org', id] });
      await queryClient.invalidateQueries({ queryKey: ['org-migration-items', id] });
    },
  });

  if (!isSuperadmin) {
    return (
      <Card>
        <EmptyState
          icon={<Ban className="size-8" />}
          title="Superadmin only"
          description="Moving equipment between organizations is a platform operation. It is not scoped to one tenant, so no organization role can hold it."
        />
      </Card>
    );
  }

  if (itemsQuery.isLoading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (itemsQuery.error) return <ErrorNotice error={itemsQuery.error} />;
  const source = itemsQuery.data;
  if (!source) return null;

  const targets = (orgsQuery.data?.items ?? []).filter(
    (o) => o.id !== id && o.status === 'active' && !o.purgeAfter,
  );
  const targetOrg = targets.find((o) => o.id === targetOrgId) ?? null;

  const toggle = (key: string): void =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const ladder = orgMigrationConfirmation(disposition);
  const confirmed =
    plan !== null &&
    Number(typedCount) === plan.movableCount &&
    typedTarget.trim() === plan.target.name.trim() &&
    (!ladder.sourceName || typedSource.trim() === plan.source.name.trim()) &&
    (!ladder.phrase || typedPhrase.trim().toUpperCase() === ladder.phrase);

  return (
    <>
      <button
        onClick={() => navigate(`/organizations/${id}`)}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Back to {source.org.name}
      </button>

      <div className="mb-5">
        <h1 className="flex flex-wrap items-center gap-3 text-xl font-semibold text-ink">
          <ArrowRightLeft className="size-5 text-muted" />
          Move {source.org.name}&rsquo;s equipment
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted">
          This changes who OWNS the records and nothing else. No chassis is contacted, no
          configuration is pushed, nothing is written at the ACS — an OLT that changes hands is
          running exactly what it was running a second earlier.
        </p>
        {source.purgeAfter && (
          <p className="mt-2 inline-flex items-center gap-2 rounded-lg border border-warn/30 bg-warn-soft px-3 py-1.5 text-xs text-warn">
            <TriangleAlert className="size-3.5" />
            This organization is archived and scheduled for deletion{' '}
            {formatTimestamp(source.purgeAfter)}.
          </p>
        )}
      </div>

      {step === 'choose' && (
        <div className="grid gap-5">
          <Card title="Where it goes">
            <div className="grid gap-4 p-5 sm:max-w-md">
              <Field
                label="Receiving organization"
                hint="Active organizations only — somebody has to be able to operate what arrives."
              >
                <SearchSelect
                  value={targetOrgId}
                  onChange={setTargetOrgId}
                  placeholder="Choose an organization…"
                  emptyLabel="No other active organization"
                  options={targets.map((o) => ({ value: o.id, label: o.name, hint: o.slug }))}
                />
              </Field>
            </div>
          </Card>

          <Card
            title={`What goes — ${picked.size} selected`}
            action={
              picked.size > 0 ? (
                <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())}>
                  Clear
                </Button>
              ) : undefined
            }
          >
            {source.groups.length === 0 ? (
              <EmptyState
                title="Nothing here can move"
                description={`${source.org.name} holds ${listCounted(source.holds)}, and none of it is equipment this can hand over.`}
              />
            ) : (
              <div className="divide-y divide-line">
                {source.groups.map((group) => (
                  <div key={group.kind} className="px-5 py-4">
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">
                        {group.label}
                      </h3>
                      <button
                        className="text-xs text-muted hover:text-ink"
                        onClick={() =>
                          setPicked((prev) => {
                            const next = new Set(prev);
                            const keys = group.items.map((i) => `${i.kind} ${i.id}`);
                            const allOn = keys.every((k) => next.has(k));
                            for (const k of keys) {
                              if (allOn) next.delete(k);
                              else next.add(k);
                            }
                            return next;
                          })
                        }
                      >
                        {group.items.every((i) => picked.has(`${i.kind} ${i.id}`))
                          ? 'None'
                          : 'All'}
                      </button>
                    </div>
                    <ul className="grid gap-1.5">
                      {group.items.map((item) => {
                        const key = `${item.kind} ${item.id}`;
                        return (
                          <li key={key}>
                            <label className="flex cursor-pointer items-start gap-3 rounded-lg px-2 py-1.5 hover:bg-surface-2">
                              <input
                                type="checkbox"
                                className="mt-1"
                                checked={picked.has(key)}
                                onChange={() => toggle(key)}
                              />
                              <span className="min-w-0">
                                <span className="flex flex-wrap items-baseline gap-2 text-sm text-ink">
                                  {item.name}
                                  {item.number !== null && (
                                    <span className="text-xs text-faint">#{item.number}</span>
                                  )}
                                  {/* A child ticked without its parent is refused
                                      by the plan; saying so here saves a round trip. */}
                                  {item.parentId && !picked.has(`${item.kind} ${item.parentId}`) && (
                                    <Pill tone="warn">needs its parent</Pill>
                                  )}
                                </span>
                                {item.detail && (
                                  <span className="block text-xs text-muted">{item.detail}</span>
                                )}
                                {item.carries.length > 0 && (
                                  <span className="block text-xs text-faint">
                                    carries {carryLine(item.carries)}
                                  </span>
                                )}
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {buildPlan.error && <ErrorNotice error={buildPlan.error} />}

          <div className="flex justify-end">
            <Button
              variant="primary"
              loading={buildPlan.isPending}
              disabled={!targetOrgId || picked.size === 0}
              onClick={() => buildPlan.mutate()}
            >
              Review what would move
            </Button>
          </div>
        </div>
      )}

      {step === 'review' && plan && (
        <div className="grid gap-5">
          <Card title={`${plan.source.name} → ${plan.target.name}`}>
            <div className="grid gap-4 p-5">
              <p className="text-sm text-muted">
                <span className="font-medium text-ink">{plan.movableCount}</span> of{' '}
                {plan.items.length} selected items would move, carrying{' '}
                <span className="font-medium text-ink">{plan.rowCount}</span> records with them.
                Nothing has been written — this is a dry run, and it expires{' '}
                {formatTimestamp(plan.expiresAt)}.
              </p>

              <ul className="grid gap-2">
                {plan.items.map((item) => (
                  <li
                    key={`${item.kind} ${item.id}`}
                    className={`rounded-lg border px-4 py-3 ${
                      item.movable ? 'border-line bg-surface-2' : 'border-danger/30 bg-danger-soft'
                    }`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="flex items-center gap-2 text-sm font-medium text-ink">
                        {item.movable ? (
                          <CircleCheck className="size-4 text-success" />
                        ) : (
                          <CircleSlash className="size-4 text-danger" />
                        )}
                        {item.name}
                      </span>
                      <Pill tone="neutral">{ORG_MIGRATION_KIND_LABELS[item.kind].one}</Pill>
                    </div>
                    {item.carries.length > 0 && (
                      <p className="mt-1 pl-6 text-xs text-muted">
                        Takes {carryLine(item.carries)} with it.
                      </p>
                    )}
                    {item.refusals.map((r) => (
                      <p key={r.code + r.message} className="mt-1 pl-6 text-xs text-danger">
                        {r.message}
                      </p>
                    ))}
                    {item.warnings.map((w) => (
                      <p key={w} className="mt-1 pl-6 text-xs text-warn">
                        {w}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>

              {plan.warnings.length > 0 && (
                <div className="rounded-lg border border-info/30 bg-info-soft px-4 py-3">
                  <p className="flex items-center gap-2 text-sm font-medium text-info">
                    <Info className="size-4" />
                    What this does not do
                  </p>
                  <ul className="mt-2 list-disc space-y-1 pl-9 text-xs text-muted">
                    {plan.warnings.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <p className="text-sm text-muted">
                  Afterwards, {plan.source.name} would still hold{' '}
                  <span className="text-ink">{listCounted(plan.remaining)}</span>.
                </p>
              </div>
            </div>
          </Card>

          <Card title={`And then ${plan.source.name}…`}>
            <div className="grid gap-3 p-5">
              {(
                [
                  [
                    'keep',
                    'Leave it as it is',
                    'The organization carries on exactly as before, minus what moved.',
                  ],
                  [
                    'archive',
                    `Archive it — suspend now, delete in ${ORG_ARCHIVE_GRACE_DAYS} days`,
                    `Its people are walled out immediately and a deletion date is stamped on it. Reversible for the whole ${ORG_ARCHIVE_GRACE_DAYS} days from the organization's own page.`,
                  ],
                  [
                    'delete_now',
                    'Delete it now',
                    'Permanent, with no copy anywhere else. Refused while any OLT, ONU or TR-069 device is still attached to it.',
                  ],
                ] as const
              ).map(([value, label, hint]) => (
                <label
                  key={value}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 ${
                    disposition === value
                      ? value === 'delete_now'
                        ? 'border-danger/40 bg-danger-soft'
                        : 'border-primary/40 bg-primary-soft'
                      : 'border-line'
                  }`}
                >
                  <input
                    type="radio"
                    name="disposition"
                    className="mt-1"
                    checked={disposition === value}
                    onChange={() => {
                      setDisposition(value);
                      setTypedSource('');
                      setTypedPhrase('');
                    }}
                  />
                  <span>
                    <span className="block text-sm font-medium text-ink">{label}</span>
                    <span className="block text-xs text-muted">{hint}</span>
                  </span>
                </label>
              ))}

              {disposition !== 'keep' && (
                <Field label="Why (kept on the organization, not only in the trail)">
                  <Input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Bought by Target Fibre, 2026-08"
                  />
                </Field>
              )}
            </div>
          </Card>

          <Card title="Confirm">
            <div className="grid gap-4 p-5 sm:max-w-lg">
              <Field
                label={`Type ${plan.movableCount} — the number of items this moves`}
                hint="An operator who types the number has read the number."
              >
                <Input
                  value={typedCount}
                  onChange={(e) => setTypedCount(e.target.value)}
                  inputMode="numeric"
                  autoComplete="off"
                />
              </Field>
              <Field label={`Type “${plan.target.name}” — the organization receiving this`}>
                <Input
                  value={typedTarget}
                  onChange={(e) => setTypedTarget(e.target.value)}
                  placeholder={plan.target.name}
                  autoComplete="off"
                />
              </Field>
              {ladder.sourceName && (
                <Field label={`Type “${plan.source.name}” — the organization being ${disposition === 'archive' ? 'archived' : 'deleted'}`}>
                  <Input
                    value={typedSource}
                    onChange={(e) => setTypedSource(e.target.value)}
                    placeholder={plan.source.name}
                    autoComplete="off"
                  />
                </Field>
              )}
              {ladder.phrase && (
                <Field
                  label={`Type ${ladder.phrase}`}
                  hint="Different acts are confirmed with different words."
                >
                  <Input
                    value={typedPhrase}
                    onChange={(e) => setTypedPhrase(e.target.value)}
                    placeholder={ladder.phrase}
                    autoComplete="off"
                  />
                </Field>
              )}
            </div>
          </Card>

          {apply.error && <ErrorNotice error={apply.error} />}

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep('choose')}>
              Back to the selection
            </Button>
            <Button
              variant={disposition === 'delete_now' ? 'danger' : 'primary'}
              loading={apply.isPending}
              disabled={!confirmed || plan.movableCount === 0}
              icon={disposition === 'delete_now' ? <Trash2 className="size-4" /> : undefined}
              onClick={() => apply.mutate()}
            >
              Move {plan.movableCount} item{plan.movableCount === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      )}

      {step === 'done' && result && (
        <div className="grid gap-5">
          <Card title="What happened">
            <div className="grid gap-4 p-5">
              <p className="text-sm text-muted">
                <span className="font-medium text-ink">{result.movedCount}</span> moved to{' '}
                {result.target.name}
                {result.skippedCount > 0 && ` · ${result.skippedCount} skipped`}
                {result.failedCount > 0 && ` · ${result.failedCount} failed`}.
              </p>

              <ul className="grid gap-2">
                {result.items.map((item) => (
                  <li
                    key={`${item.kind} ${item.id}`}
                    className={`rounded-lg border px-4 py-3 ${
                      item.outcome === 'moved'
                        ? 'border-success/30 bg-success-soft'
                        : item.outcome === 'failed'
                          ? 'border-danger/30 bg-danger-soft'
                          : 'border-line bg-surface-2'
                    }`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-ink">{item.name}</span>
                      <Pill state
                        tone={
                          item.outcome === 'moved'
                            ? 'success'
                            : item.outcome === 'failed'
                              ? 'danger'
                              : 'neutral'
                        }
                      >
                        {item.outcome}
                      </Pill>
                    </div>
                    {item.moved.length > 0 && (
                      <p className="mt-1 text-xs text-muted">Took {carryLine(item.moved)}.</p>
                    )}
                    {item.message && <p className="mt-1 text-xs text-muted">{item.message}</p>}
                    {item.warnings.map((w) => (
                      <p key={w} className="mt-1 text-xs text-warn">
                        {w}
                      </p>
                    ))}
                  </li>
                ))}
              </ul>

              <div className="rounded-lg border border-line bg-surface-2 px-4 py-3">
                <p className="text-sm text-ink">{result.sourceOutcome}</p>
                {result.purgeAfter && (
                  <p className="mt-1 text-xs text-muted">
                    Scheduled deletion: {formatTimestamp(result.purgeAfter)}. Un-archive it from its
                    own page any time before then.
                  </p>
                )}
                {result.remaining.length > 0 && (
                  <p className="mt-1 text-xs text-muted">
                    Still attached: {listCounted(result.remaining)}.
                  </p>
                )}
              </div>
            </div>
          </Card>

          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setPicked(new Set());
                setPlan(null);
                setResult(null);
                setStep('choose');
                void itemsQuery.refetch();
              }}
            >
              Move something else
            </Button>
            <Button variant="primary" onClick={() => navigate('/organizations')}>
              Done
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
